// app/components/Plugins/Hedging/riskAssessment.ts

export interface Asset {
  symbol: string
  shares: number
  industry: string
  avgPrice: number
  currentPrice: number
  pnl: number
}

export interface IndustryExposure {
  [industry: string]: number // Exposure in percentage
}

/**
 * Calculate the total portfolio value by summing up each asset's value.
 */
export const calculatePortfolioValue = (assets: Asset[]): number => {
  return assets.reduce(
    (total, asset) => total + asset.shares * asset.currentPrice,
    0
  )
}

/**
 * Calculate the percentage exposure for each industry.
 */
export const calculateIndustryExposure = (
  assets: Asset[]
): IndustryExposure => {
  const totalValue = calculatePortfolioValue(assets)
  const exposure: IndustryExposure = {}

  assets.forEach(asset => {
    const assetValue = asset.shares * asset.currentPrice
    exposure[asset.industry] = (exposure[asset.industry] || 0) + assetValue
  })

  // Convert each industry value to a percentage of the total portfolio value.
  for (const industry in exposure) {
    exposure[industry] = (exposure[industry] / totalValue) * 100
  }
  return exposure
}

/**
 * Define risk weights per industry. These weights can be fine-tuned for the Indian market.
 * For instance, if Pharma is seen as more volatile, we might give it a higher weight.
 */
const riskWeights: { [industry: string]: number } = {
  // IT: 1.2,
  // Energy: 1.1,
  // Banking: 1.0,
  // Pharma: 1.3,
  FMCG: 0.6, // Consumer staples - very stable
  Banking: 0.7, // Financial sector - relatively stable
  IT: 0.8, // Technology - moderate stability
  Insurance: 0.7, // Financial sector - stable
  Energy: 0.9, // Utilities - moderate volatility
  Steel: 0.9, // Commodities - moderate volatility
  Pharma: 0.85, // Healthcare - moderate stability
  Auto: 0.9, // Cyclical but established
  Infrastructure: 1.0, // Higher volatility
  RealEstate: 1.1 // Higher volatility
  // Any other industry defaults to a weight of 1.0
}

/**
 * Assess risk by calculating a weighted sum of the industry exposures.
 * The result is normalized to a 0-100 scale.
 */
export const assessRisk = (assets: Asset[]): number => {
  const exposure = calculateIndustryExposure(assets)
  let riskScore = 0
  for (const industry in exposure) {
    const weight = riskWeights[industry] || 1.0
    riskScore += exposure[industry] * weight
  }
  // For simplicity, if the riskScore exceeds 100, cap it at 100.
  return riskScore > 100 ? 100 : riskScore
}

/**
 * Return detailed metrics for the portfolio.
 */
export const getDetailedMetrics = (assets: Asset[]) => {
  const totalValue = calculatePortfolioValue(assets)
  const industryExposure = calculateIndustryExposure(assets)
  const riskScore = assessRisk(assets)
  return { totalValue, industryExposure, riskScore }
}
